import React, { useEffect, useState } from "react";
import { LogOut, Languages, Bell } from "lucide-react";
import { useI18n } from "@/contexts/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

export default function Header() {
  const { t, lang, setLang, role } = useI18n();
  const { user, logout } = useAuth();
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(id);
  }, []);

  if (!user) return null;
  const localeStr = lang === "pashto" ? "ps-AF" : "fa-AF";
  const dateStr = now.toLocaleDateString(localeStr, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const timeStr = now.toLocaleTimeString(localeStr, { hour: "2-digit", minute: "2-digit" });

  const initial = (user.name || "?").trim().charAt(0);

  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-30 bg-white/80 backdrop-blur-xl border-b border-gray-100"
    >
      <div className="h-16 px-4 md:px-8 flex items-center justify-between">
        <div className="flex items-center gap-3 pr-12 md:pr-0">
          <div className="hidden md:block">
            <div className="text-sm text-gray-500">{dateStr}</div>
            <div className="text-base font-semibold text-gray-900 tabular">{timeStr}</div>
          </div>
        </div>

        <div className="flex items-center gap-2 md:gap-3">
          {/* Language Switcher */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="lang-switcher"
                className="flex items-center gap-2 px-3 py-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-sm font-medium text-gray-700"
              >
                <Languages className="w-4 h-4" />
                {lang === "dari" ? "دری" : "پښتو"}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[140px]">
              <DropdownMenuItem
                data-testid="lang-dari"
                onClick={() => setLang("dari")}
                className={lang === "dari" ? "bg-gray-100 font-bold" : ""}
              >
                دری / Dari
              </DropdownMenuItem>
              <DropdownMenuItem
                data-testid="lang-pashto"
                onClick={() => setLang("pashto")}
                className={lang === "pashto" ? "bg-gray-100 font-bold" : ""}
              >
                پښتو / Pashto
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <button className="p-2 rounded-xl border border-gray-200 hover:bg-gray-50 text-gray-600 relative">
            <Bell className="w-4 h-4" />
          </button>

          {/* Profile */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="profile-menu"
                className="flex items-center gap-3 pr-2 pl-1 py-1 rounded-xl border border-gray-200 hover:bg-gray-50"
              >
                <div className="text-right hidden sm:block leading-tight">
                  <div className="text-sm font-bold text-gray-900">{user.name}</div>
                  <div className="text-xs text-gray-500">{role(user.role)}</div>
                </div>
                <div className="w-9 h-9 rounded-xl bg-[#071A33] text-white grid place-items-center font-bold">
                  {initial}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="min-w-[180px]">
              <DropdownMenuLabel>{user.name}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => {
                  logout();
                  window.location.href = "/login";
                }}
                data-testid="logout-btn"
                className="logout-menu-item text-red-600 focus:bg-[#7F1020] focus:text-white data-[highlighted]:bg-[#7F1020] data-[highlighted]:text-white"
              >
                <LogOut className="w-4 h-4 ml-2" />
                {t("logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
